import { useDeferredValue, useEffect, useMemo, useState, startTransition } from "react"

import {
  apiClient,
  type FleetQrRegisterRequest,
  type FleetQrRegisterResponse,
  type FleetCertificationRecord,
  isApiError,
  type OperatorBreakdownMetrics,
  type OperatorIncidentEscalationPacketRecord,
  type OperatorHotspotItem,
  type OperatorIncidentEscalationShareResponse,
  type OperatorIncidentRecord,
  type OperatorPerformanceMetrics,
  type OperatorTimelinePoint,
  type PublicLookupData,
} from "@/lib/api/client"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type {
  OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"
import {
  canManagePortalOrganizations,
  getOperatorSessionOrganization,
  resolveOperatorSession,
} from "@/features/operator/shared/operator-access"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  useOptionalOperatorShellSession,
} from "@/features/operator/shared/operator-shell-session-context"

const FLEET_INCIDENT_STATUSES = ["all", "pending", "in_progress", "resolved"] as const
const FLEET_ANALYTICS_WINDOWS = ["7", "30", "90"] as const
const FLEET_CERTIFICATION_SORTS = ["artifact", "expiring", "score"] as const
const FLEET_QR_DEFAULT_COUNTRY = "India"
const FLEET_DASHBOARD_POLL_MS = 45_000

export interface FleetDashboardSearch {
  assignmentStatus?: string
  incidentQuery?: string
  incidentStatus?: (typeof FLEET_INCIDENT_STATUSES)[number]
  memberType?: string
  tagQuery?: string
  tagStatus?: string
}

export interface FleetAnalyticsSearch {
  days?: (typeof FLEET_ANALYTICS_WINDOWS)[number]
}

export interface FleetCertificationsSearch {
  program?: string
  q?: string
  sort?: (typeof FLEET_CERTIFICATION_SORTS)[number]
  status?: string
}

type FleetQrActivationMode = "activated" | "registration_required"

interface FleetQrRegistryFormState {
  activationMode: FleetQrActivationMode
  companyName: string
  country: string
  fleetName: string
  fleetSize: string
  notificationEmail: string
  organizationCity: string
  organizationName: string
  organizationRegion: string
  quantity: string
  startingNumber: string
  truckPrefix: string
  vehicleMake: string
  vehicleModel: string
  vehicleYear: string
}

interface FleetWorkspaceProps {
  incidentId?: number
  mode: "analytics" | "certifications" | "dashboard" | "tags"
  onSearchChange: (
    next: Partial<
      FleetAnalyticsSearch & FleetCertificationsSearch & FleetDashboardSearch
    >
  ) => void
  onSelectIncident: (incidentId: number) => void
  search: FleetAnalyticsSearch | FleetCertificationsSearch | FleetDashboardSearch
}

interface FleetDashboardBundle {
  incidents: OperatorIncidentRecord[]
  inventory: PublicLookupData[]
  scope: FleetWorkspaceScope
  stats: {
    by_ai_status?: Array<{ _count: number; aiStatus: string }>
    by_fraud_status?: Array<{ _count: number; fraudStatus: string }>
    by_incident_status?: Array<{ _count: number; incidentStatus: string }>
    by_report_kind?: Array<{ _count: number; reportKind: string }>
    by_severity?: Array<{ _count: number; severity: string }>
    by_type?: Array<{ _count: number; type: string }>
    by_verification_status?: Array<{
      _count: number
      verificationStatus: string
    }>
    critical_or_severe_open?: number
    in_progress?: number
    needs_attention_now?: number
    open?: number
    pending?: number
    recent_24h?: number
    resolved?: number
    total?: number
  } | null
}

interface FleetAnalyticsBundle {
  breakdown: OperatorBreakdownMetrics | null
  hotspots: OperatorHotspotItem[]
  hourlyDistribution: Array<{ count: number; hour: number }>
  incidents: OperatorIncidentRecord[]
  performance: OperatorPerformanceMetrics | null
  scope: FleetAnalyticsScope
  safetyScores: Array<{
    incidentCount: number
    tag: string
    totalImpact: number
  }>
  stats: FleetDashboardBundle["stats"]
  timeline: OperatorTimelinePoint[]
}

interface FleetCertificationsBundle {
  certifications: FleetCertificationRecord[]
  scope: FleetWorkspaceScope
}

interface FleetIncidentDetailBundle {
  detail: OperatorIncidentRecord
}

interface FleetIncidentEscalationChannelState {
  email: boolean
  sms: boolean
  whatsapp: boolean
}

interface FleetIncidentEscalationPacketState
  extends OperatorIncidentEscalationPacketRecord {
  attempts?: OperatorIncidentEscalationShareResponse["attempts"]
  failureCount?: number
  shareStatus?: OperatorIncidentEscalationShareResponse["shareStatus"] | "revoked"
  successCount?: number
}

interface FleetAnalyticsScope {
  kind: "global_authenticated" | "global_guest" | "organization"
  operatorLabel: string
  organizationId?: string
  organizationName?: string | null
}

interface FleetWorkspaceScope {
  kind: "global" | "organization"
  operatorLabel: string
  organizationId?: string
  organizationName?: string | null
}

function slugifyFleetOrganization(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

function createInitialFleetQrRegistryForm(
  scope?: FleetWorkspaceScope | null
): FleetQrRegistryFormState {
  return {
    activationMode: "activated",
    companyName: scope?.organizationName || "",
    country: FLEET_QR_DEFAULT_COUNTRY,
    fleetName: scope?.organizationName || "",
    fleetSize: "10",
    notificationEmail: "ml@feedbacknfc.com",
    organizationCity: "Bengaluru",
    organizationName: scope?.organizationName || "",
    organizationRegion: "Karnataka",
    quantity: "10",
    startingNumber: "1",
    truckPrefix: "TRUCK",
    vehicleMake: "Tata",
    vehicleModel: "Ultra",
    vehicleYear: String(new Date().getFullYear()),
  }
}

function buildFleetQrRows(form: FleetQrRegistryFormState) {
  const quantity = Number.parseInt(form.quantity, 10)
  const startingNumber = Number.parseInt(form.startingNumber, 10)
  const year = Number.parseInt(form.vehicleYear, 10)
  const numberWidth = Math.max(3, String(startingNumber + quantity - 1).length)

  return Array.from({ length: quantity }, (_, index) => {
    const serialNumber = startingNumber + index
    const truckNumber = `${form.truckPrefix.trim()}-${String(serialNumber).padStart(numberWidth, "0")}`

    return {
      assignmentStatus: form.activationMode === "activated" ? "activated" : "assigned",
      contactEmail: form.notificationEmail.trim() || undefined,
      contactName: form.companyName.trim() || form.organizationName.trim() || undefined,
      externalRef: truckNumber,
      fleetName: form.fleetName.trim() || form.organizationName.trim() || undefined,
      memberType: "truck",
      plateNumber: truckNumber,
      status: "active",
      vehicleMake: form.vehicleMake.trim() || undefined,
      vehicleModel: form.vehicleModel.trim() || undefined,
      vehicleType: "truck",
      vehicleYear: Number.isFinite(year) ? year : undefined,
    }
  })
}

function normalizeToken(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

function titleizeToken(value: string | null | undefined) {
  const normalized = normalizeToken(value)
  if (!normalized) {
    return "Unknown"
  }

  return normalized
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function resolveIncidentStatus(incident: OperatorIncidentRecord) {
  return (
    incident.incidentLifecycle?.incidentStatus
    ?? incident.incidentStatus
    ?? incident.status
    ?? "reported"
  )
}

function isOpenIncident(incident: OperatorIncidentRecord) {
  const normalizedStatus = normalizeToken(resolveIncidentStatus(incident))
  return normalizedStatus !== "resolved" && normalizedStatus !== "closed"
}

function isCriticalOrSevereIncident(incident: OperatorIncidentRecord) {
  const normalizedSeverity = normalizeToken(incident.severity)
  return normalizedSeverity === "critical" || normalizedSeverity === "severe"
}

function requiresAttentionNow(incident: OperatorIncidentRecord) {
  if (!isOpenIncident(incident)) {
    return false
  }

  if (isCriticalOrSevereIncident(incident)) {
    return true
  }

  const verificationStatus = normalizeToken(
    incident.intelligence?.verification?.status
  )
  if (["pending", "review", "rejected", "failed", "mismatch"].includes(verificationStatus)) {
    return true
  }

  const fraudStatus = normalizeToken(incident.intelligence?.fraud?.status)
  if (["review", "suspicious", "high_risk", "flagged"].includes(fraudStatus)) {
    return true
  }

  const aiStatus = normalizeToken(incident.intelligence?.ai?.status)
  return ["review", "failed", "error"].includes(aiStatus)
}

function getIncidentReportedTime(incident: OperatorIncidentRecord) {
  return (
    incident.incidentLifecycle?.timestamps?.reportedAt
    ?? incident.createdAt
    ?? null
  )
}

function toFiniteCoordinateValue(value: unknown) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function formatCoordinates(latitude?: number | string | null, longitude?: number | string | null) {
  const normalizedLatitude = toFiniteCoordinateValue(latitude)
  const normalizedLongitude = toFiniteCoordinateValue(longitude)

  if (normalizedLatitude == null || normalizedLongitude == null) {
    return "Not recorded"
  }

  return `${normalizedLatitude.toFixed(5)}, ${normalizedLongitude.toFixed(5)}`
}

function buildCoordinatesMapHref(
  latitude?: number | string | null,
  longitude?: number | string | null
) {
  const normalizedLatitude = toFiniteCoordinateValue(latitude)
  const normalizedLongitude = toFiniteCoordinateValue(longitude)

  if (normalizedLatitude == null || normalizedLongitude == null) {
    return null
  }

  return `https://www.google.com/maps?q=${normalizedLatitude},${normalizedLongitude}`
}

function isLikelyPhotoEvidenceMedia(mimeType?: string | null) {
  if (!mimeType) {
    return true
  }

  return mimeType.toLowerCase().startsWith("image/")
}

function getIncidentLocationLabel(incident: OperatorIncidentRecord) {
  return (
    incident.location?.name
    || incident.locationDescription
    || incident.jurisdiction?.name
    || "Not recorded"
  )
}

function getIncidentSourceLabel(incident: OperatorIncidentRecord) {
  return (
    incident.subjectAttribution?.label
    || incident.contact?.name
    || incident.device?.tag
    || incident.stickerId
    || "Unknown source"
  )
}

function hasIncidentEscalationPacketHistory(incident: OperatorIncidentRecord) {
  if (incident.escalationPacket?.packetId) {
    return true
  }

  return (incident.exportPackages ?? []).some((exportPackage) => {
    return exportPackage.recipientKey === "incident_escalation_packet_v1"
      || exportPackage.purposeLabel === "incident_escalation_packet_share"
  })
}

function buildIncidentEscalationPacketStateFromRecord(
  incident: OperatorIncidentRecord | null | undefined
): FleetIncidentEscalationPacketState | null {
  const packet = incident?.escalationPacket

  if (!packet?.packetId) {
    return null
  }

  return {
    ...packet,
    shareStatus: packet.revoked ? "revoked" : packet.sharedAt ? "success" : undefined,
  }
}

function mergeIncidentEscalationPacketState(
  persistedState: FleetIncidentEscalationPacketState | null,
  sessionState: FleetIncidentEscalationPacketState | null
): FleetIncidentEscalationPacketState | null {
  if (!persistedState) {
    return sessionState
  }

  if (!sessionState) {
    return persistedState
  }

  return {
    ...sessionState,
    ...persistedState,
    attempts: sessionState.attempts ?? persistedState.attempts,
    failureCount: sessionState.failureCount ?? persistedState.failureCount,
    shareStatus: sessionState.shareStatus ?? persistedState.shareStatus,
    successCount: sessionState.successCount ?? persistedState.successCount,
  }
}

function parseIncidentEscalationPacketToken(packetUrl?: string | null) {
  if (!packetUrl) {
    return null
  }

  try {
    const parsed = new URL(packetUrl)
    return parsed.pathname.split("/").filter(Boolean).at(-1) ?? null
  } catch {
    return packetUrl.split("/").filter(Boolean).at(-1) ?? null
  }
}

function resolveFleetWorkspaceScopeFromSession(
  session: OperatorResolvedSession
): FleetWorkspaceScope {
  const organization = getOperatorSessionOrganization(session)
  const operatorLabel = session.displayName || "Authenticated operator"

  if (organization.id) {
    return {
      kind: "organization",
      operatorLabel,
      organizationId: organization.id,
      organizationName: organization.name || "Assigned organization",
    } satisfies FleetWorkspaceScope
  }

  if (canManagePortalOrganizations(session)) {
    return {
      kind: "global",
      operatorLabel,
    } satisfies FleetWorkspaceScope
  }

  throw new Error(
    "Fleet access requires an organization-scoped operator session or global operator access."
  )
}

async function resolveFleetWorkspaceScope(
  shellSessionState?: ReturnType<typeof useOptionalOperatorShellSession>
) {
  if (shellSessionState?.status === "error") {
    throw new Error(shellSessionState.message)
  }

  if (shellSessionState?.status === "ready") {
    return resolveFleetWorkspaceScopeFromSession(shellSessionState.session)
  }

  const session = await resolveOperatorSession()

  return resolveFleetWorkspaceScopeFromSession(session)
}

async function loadFleetDashboardBundle(
  shellSessionState?: ReturnType<typeof useOptionalOperatorShellSession>
) {
  const scope = await resolveFleetWorkspaceScope(shellSessionState)
  const [stats, incidents, inventory] = await Promise.all([
    apiClient.getOperatorIncidentStats(),
    apiClient.getOperatorIncidents({ limit: 120 }),
    apiClient.getFleetInventory(200),
  ])

  return {
    incidents: incidents.data ?? [],
    inventory: inventory.data ?? [],
    scope,
    stats: stats.data ?? null,
  } satisfies FleetDashboardBundle
}

function resolveFleetAnalyticsScopeFromSession(
  session: OperatorResolvedSession
): FleetAnalyticsScope {
  const organization = getOperatorSessionOrganization(session)
  const organizationId = organization.id
  const organizationName = organization.name

  if (!organizationId) {
    return {
      kind: "global_authenticated",
      operatorLabel: session.displayName || "Authenticated operator",
    } satisfies FleetAnalyticsScope
  }

  return {
    kind: "organization",
    operatorLabel: session.displayName || "Authenticated operator",
    organizationId,
    organizationName: organizationName || "Assigned organization",
  } satisfies FleetAnalyticsScope
}

async function resolveFleetAnalyticsScope(
  shellSessionState: ReturnType<typeof useOptionalOperatorShellSession>
) {
  if (shellSessionState?.status === "ready") {
    return resolveFleetAnalyticsScopeFromSession(shellSessionState.session)
  }

  if (shellSessionState?.status === "error" || shellSessionState?.status === "loading") {
    return {
      kind: "global_guest",
      operatorLabel: "Guest or unsigned session",
    } satisfies FleetAnalyticsScope
  }

  try {
    const profile = await apiClient.getOperatorUserProfile()
    const organizationId = profile.data.organization?.id

    if (!organizationId) {
      return {
        kind: "global_authenticated",
        operatorLabel:
          profile.data.name || profile.data.email || "Authenticated operator",
      } satisfies FleetAnalyticsScope
    }

    return {
      kind: "organization",
      operatorLabel:
        profile.data.name || profile.data.email || "Authenticated operator",
      organizationId,
      organizationName:
        profile.data.organization?.name || "Assigned organization",
    } satisfies FleetAnalyticsScope
  } catch (error) {
    if (isApiError(error) && (error.status === 401 || error.status === 403)) {
      return {
        kind: "global_guest",
        operatorLabel: "Guest or unsigned session",
      } satisfies FleetAnalyticsScope
    }

    return {
      kind: "global_authenticated",
      operatorLabel: "Authenticated operator",
    } satisfies FleetAnalyticsScope
  }
}

async function loadFleetAnalyticsBundle(
  days: number,
  shellSessionState: ReturnType<typeof useOptionalOperatorShellSession>
) {
  const scope = await resolveFleetAnalyticsScope(shellSessionState)
  const organizationId =
    scope.kind === "organization" ? scope.organizationId : undefined
  const [stats, incidents, timeline, hourly, safety, hotspots, performance, breakdown] =
    await Promise.all([
      apiClient.getOperatorIncidentStats(),
      apiClient.getOperatorIncidents({ limit: 80 }),
      apiClient.getOperatorTimeline(`${days}d`, organizationId),
      apiClient.getOperatorHourlyDistribution(days, organizationId),
      apiClient.getOperatorSafetyScores(days, 8, organizationId),
      apiClient.getOperatorHotspots(days, 8, organizationId),
      apiClient.getOperatorPerformance(organizationId),
      apiClient.getOperatorBreakdown(organizationId, days),
    ])

  return {
    breakdown: breakdown.data ?? null,
    hotspots: hotspots.data?.hotspots ?? [],
    hourlyDistribution: hourly.data ?? [],
    incidents: incidents.data ?? [],
    performance: performance.data ?? null,
    scope,
    safetyScores: (safety.data?.items ?? []).map((item) => ({
      incidentCount: item.incidentCount,
      tag: item.tag,
      totalImpact: item.totalImpact,
    })),
    stats: stats.data ?? null,
    timeline: timeline.data?.timeline ?? [],
  } satisfies FleetAnalyticsBundle
}

async function loadFleetCertificationsBundle(
  shellSessionState?: ReturnType<typeof useOptionalOperatorShellSession>
) {
  const scope = await resolveFleetWorkspaceScope(shellSessionState)
  const certifications = await apiClient.getFleetCertifications({
    includeEvidence: true,
    organizationId:
      scope.kind === "organization" ? scope.organizationId : undefined,
  })

  return {
    certifications: certifications.data ?? [],
    scope,
  } satisfies FleetCertificationsBundle
}

async function loadFleetIncidentDetail(incidentId: number) {
  const detail = await apiClient.getOperatorIncident(incidentId)

  return {
    detail: detail.data,
  } satisfies FleetIncidentDetailBundle
}

function filterFleetIncidents(
  incidents: OperatorIncidentRecord[],
  search: FleetDashboardSearch
) {
  const query = normalizeToken(search.incidentQuery)

  return incidents.filter((incident) => {
    if (
      search.incidentStatus
      && search.incidentStatus !== "all"
      && normalizeToken(
        incident.incidentLifecycle?.incidentStatus ?? incident.incidentStatus ?? incident.status
      ) !== normalizeToken(search.incidentStatus)
    ) {
      return false
    }

    if (!query) {
      return true
    }

    const haystack = normalizeToken(
      [
        incident.id,
        incident.type,
        incident.reportKind,
        incident.description,
        incident.locationDescription,
        incident.vehicle?.plateNumber,
        incident.contact?.name,
        incident.device?.tag,
      ]
        .filter(Boolean)
        .join(" ")
    )

    return haystack.includes(query)
  })
}

function filterFleetInventory(
  inventory: PublicLookupData[],
  search: FleetDashboardSearch
) {
  const query = normalizeToken(search.tagQuery)

  return inventory.filter((item) => {
    if (
      search.memberType
      && search.memberType !== "all"
      && normalizeToken(item.assignment?.memberType) !== normalizeToken(search.memberType)
    ) {
      return false
    }

    if (
      search.assignmentStatus
      && search.assignmentStatus !== "all"
      && normalizeToken(item.assignment?.assignmentStatus)
        !== normalizeToken(search.assignmentStatus)
    ) {
      return false
    }

    if (
      search.tagStatus
      && search.tagStatus !== "all"
      && normalizeToken(item.device?.status) !== normalizeToken(search.tagStatus)
    ) {
      return false
    }

    if (!query) {
      return true
    }

    const haystack = normalizeToken(
      [
        item.tag,
        item.assignment?.assigneeName,
        item.assignment?.externalRef,
        item.vehicle?.plateNumber,
        item.vehicle?.fleetName,
        item.contact?.name,
      ]
        .filter(Boolean)
        .join(" ")
    )

    return haystack.includes(query)
  })
}

function certificationTone(record: FleetCertificationRecord) {
  const normalized = normalizeToken(record.status)

  if (normalized === "expired") {
    return "bg-destructive/12 text-destructive"
  }

  if (normalized === "current" || normalized === "active" || normalized === "awarded") {
    return "bg-chart-3/14 text-foreground"
  }

  return "bg-secondary text-secondary-foreground"
}

function getFleetAnalyticsScopeSummary(scope: FleetAnalyticsScope) {
  if (scope.kind === "organization") {
    return {
      badge: `Fleet analytics · ${scope.organizationName || "Scoped"}`,
      description:
        "Analytics is scoped to the assigned organization for this session.",
      headline:
        `${scope.operatorLabel} is running analytics for ${scope.organizationName || "the assigned organization"}.`,
      note:
        "Organization-specific analytics are shown alongside the standard incident views.",
      title: "Organization-scoped analytics mode",
    }
  }

  if (scope.kind === "global_authenticated") {
    return {
      badge: "Fleet analytics · global",
      description:
        "Analytics is available globally for this signed-in session.",
      headline:
        `${scope.operatorLabel} is authenticated, but this surface has no organization scope.`,
      note:
        "No organization-specific filter is applied in this view.",
      title: "Global analytics mode",
    }
  }

  return {
    badge: "Fleet analytics · global",
    description:
      "Analytics is available globally when no organization-scoped session is active.",
    headline: "Guest or unsigned session is running analytics in global mode.",
    note:
      "This view shows the global analytics summary.",
    title: "Global analytics mode",
  }
}

function getFleetWorkspaceScopeSummary(
  scope: FleetWorkspaceScope,
  mode: "certifications" | "dashboard" | "tags"
) {
  if (scope.kind === "organization") {
    return {
      headline:
        `${scope.operatorLabel} is working inside ${scope.organizationName || "the assigned organization"}.`,
      note:
        mode === "dashboard"
          ? "Incident queue and fleet inventory stay limited to that organization."
          : mode === "tags"
            ? "QR generation and truck registry actions stay limited to that organization."
          : "Certification records stay limited to that organization.",
      title:
        mode === "dashboard"
          ? "Organization-scoped fleet mode"
          : mode === "tags"
            ? "Organization-scoped fleet QR mode"
          : "Organization-scoped certification mode",
    }
  }

  return {
    headline: `${scope.operatorLabel} has global Fleet access across visible organizations.`,
    note:
      mode === "dashboard"
        ? "Incident queue and fleet inventory are showing all visible organizations."
        : mode === "tags"
          ? "QR generation can create or update fleet organizations, while the registry shows all visible tagged trucks."
        : "Certification records are showing all visible organizations.",
    title:
      mode === "dashboard"
        ? "Global fleet mode"
        : mode === "tags"
          ? "Global fleet QR mode"
          : "Global certification mode",
  }
}

function FleetWorkspaceLoadingSkeleton({
  mode,
}: {
  mode: "analytics" | "certifications" | "dashboard" | "tags"
}) {
  return (
    <div className="flex flex-col gap-5" data-testid="fleet-workspace-skeleton">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`fleet-metric-skeleton-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-8 w-16" />
            <Skeleton className="mt-3 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-4/5" />
          </div>
        ))}
      </section>

      {mode === "analytics" ? (
        <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-5/6" />
        </div>
      ) : null}

      <section
        className={cn(
          "grid gap-5",
          mode === "dashboard"
            ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]"
            : "xl:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]"
        )}
      >
        <OperatorPanel
          title={
            mode === "analytics"
              ? "Analytics read views"
              : mode === "certifications"
                ? "Certification roster"
                : mode === "tags"
                  ? "Fleet QR registry"
                  : "Incident list"
          }
          description={
            mode === "analytics"
              ? "Review timeline, hotspots, safety scores, and breakdowns for the selected analytics window."
              : mode === "certifications"
                ? "Filter and review certification records for the active organization."
                : mode === "tags"
                  ? "Generate report-ready truck QR tags and review the fleet registry."
                  : "Operational incident feed for the active fleet organization."
          }
        >
          <div className="grid gap-4">
            <div
              className={cn(
                "grid gap-3",
                mode === "analytics" ? "md:grid-cols-4" : "md:grid-cols-2 xl:grid-cols-4"
              )}
            >
              {Array.from({ length: mode === "analytics" ? 2 : 4 }).map((_, index) => (
                <div key={`fleet-filter-skeleton-${index}`} className="grid gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full rounded-2xl" />
                </div>
              ))}
            </div>
            <div className="grid gap-3">
              {Array.from({ length: mode === "analytics" ? 6 : 4 }).map((_, index) => (
                <Skeleton
                  key={`fleet-primary-row-${index}`}
                  className="h-14 w-full rounded-2xl"
                />
              ))}
            </div>
          </div>
        </OperatorPanel>

        <div className="grid gap-5">
          <OperatorPanel
            title={
              mode === "certifications"
                ? "Certification detail"
                : mode === "analytics"
                  ? "Scope detail"
                  : mode === "tags"
                    ? "Generated tags"
                    : "Incident detail"
            }
            description={
              mode === "certifications"
                ? "Review the details of the selected certification record."
                : mode === "analytics"
                  ? "Review the current analytics scope and supporting readouts."
                  : mode === "tags"
                    ? "Review generated QR rows and current truck-tag assignments."
                    : "Review the currently selected incident details, evidence summary, and latest activity."
            }
          >
            <div className="grid gap-3">
              {Array.from({ length: mode === "dashboard" ? 5 : 4 }).map((_, index) => (
                <Skeleton
                  key={`fleet-secondary-row-${index}`}
                  className={cn(
                    "w-full rounded-2xl",
                    mode === "dashboard" && index === 0 ? "h-10" : "h-14"
                  )}
                />
              ))}
            </div>
          </OperatorPanel>

          {mode === "dashboard" ? (
            <OperatorPanel
              title="Selected incident detail"
              description="Review the currently selected incident, assignments, and updates."
            >
              <div className="grid gap-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton
                    key={`fleet-detail-row-${index}`}
                    className={cn(
                      "w-full rounded-2xl",
                      index === 0 ? "h-16" : index === 1 ? "h-24" : "h-12"
                    )}
                  />
                ))}
              </div>
            </OperatorPanel>
          ) : null}
        </div>
      </section>
    </div>
  )
}

export function FleetWorkspace({
  incidentId,
  mode,
  onSearchChange,
  onSelectIncident,
  search,
}: FleetWorkspaceProps) {
  const shellSessionState = useOptionalOperatorShellSession()
  const [dashboardBundle, setDashboardBundle] = useState<FleetDashboardBundle | null>(
    null
  )
  const [analyticsBundle, setAnalyticsBundle] = useState<FleetAnalyticsBundle | null>(
    null
  )
  const [certificationsBundle, setCertificationsBundle] =
    useState<FleetCertificationsBundle | null>(null)
  const [detailBundle, setDetailBundle] = useState<FleetIncidentDetailBundle | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isGeneratingTags, setIsGeneratingTags] = useState(false)
  const [fleetQrRegistryForm, setFleetQrRegistryForm] = useState<FleetQrRegistryFormState>(
    () => createInitialFleetQrRegistryForm()
  )
  const [lastFleetQrImport, setLastFleetQrImport] =
    useState<FleetQrRegisterResponse | null>(null)
  const [incidentPacketActionError, setIncidentPacketActionError] = useState<string | null>(null)
  const [incidentPacketActionMode, setIncidentPacketActionMode] = useState<
    "revoke" | "rotate" | "share" | null
  >(null)
  const [incidentPacketChannels, setIncidentPacketChannels] =
    useState<FleetIncidentEscalationChannelState>({
      email: true,
      sms: true,
      whatsapp: false,
    })
  const [incidentPacketStateById, setIncidentPacketStateById] = useState<
    Record<number, FleetIncidentEscalationPacketState | undefined>
  >({})
  const deferredIncidentQuery = useDeferredValue(
    "incidentQuery" in search ? search.incidentQuery ?? "" : ""
  )
  const deferredTagQuery = useDeferredValue(
    "tagQuery" in search ? search.tagQuery ?? "" : ""
  )
  const certificationQuery = useDeferredValue("q" in search ? search.q ?? "" : "")
  const analyticsWindow = "days" in search ? search.days ?? "30" : "30"

  useEffect(() => {
    if (mode !== "tags" || !dashboardBundle?.scope) {
      return
    }

    setFleetQrRegistryForm((previous) => ({
      ...previous,
      companyName:
        previous.companyName || dashboardBundle.scope.organizationName || "",
      fleetName:
        previous.fleetName || dashboardBundle.scope.organizationName || "",
      organizationName:
        previous.organizationName || dashboardBundle.scope.organizationName || "",
    }))
  }, [dashboardBundle?.scope, mode])

  useEffect(() => {
    let cancelled = false
    setError(null)
    setIsLoading(true)

    if (shellSessionState?.status === "loading" && mode !== "analytics") {
      return () => {
        cancelled = true
      }
    }

    const load = async () => {
      if (mode === "dashboard" || mode === "tags") {
        const nextDashboard = await loadFleetDashboardBundle(shellSessionState)
        if (!cancelled) {
          startTransition(() => {
            setDashboardBundle(nextDashboard)
            setAnalyticsBundle(null)
            setCertificationsBundle(null)
            setDetailBundle(null)
          })
        }
        return
      }

      if (mode === "analytics") {
        const nextAnalytics = await loadFleetAnalyticsBundle(
          Number(analyticsWindow),
          shellSessionState
        )
        if (!cancelled) {
          startTransition(() => {
            setAnalyticsBundle(nextAnalytics)
            setDashboardBundle(null)
            setCertificationsBundle(null)
            setDetailBundle(null)
          })
        }
        return
      }

      const nextCertifications = await loadFleetCertificationsBundle(shellSessionState)
      if (!cancelled) {
        startTransition(() => {
          setCertificationsBundle(nextCertifications)
          setDashboardBundle(null)
          setAnalyticsBundle(null)
          setDetailBundle(null)
        })
      }
    }

    void load()
      .catch((nextError: unknown) => {
        if (!cancelled) {
          setError(
            nextError instanceof Error
              ? nextError.message
              : "Fleet data could not be loaded."
          )
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [analyticsWindow, mode, shellSessionState])

  async function refresh() {
    setIsRefreshing(true)
    setError(null)

    try {
      if (mode === "dashboard" || mode === "tags") {
        const [nextDashboard, nextDetail] = await Promise.all([
          loadFleetDashboardBundle(shellSessionState),
          mode === "dashboard" && activeIncidentId
            ? loadFleetIncidentDetail(activeIncidentId)
            : Promise.resolve(null),
        ])
        startTransition(() => {
          setDashboardBundle(nextDashboard)
          if (nextDetail) {
            setDetailBundle(nextDetail)
          }
        })
      } else if (mode === "analytics") {
        const nextAnalytics = await loadFleetAnalyticsBundle(
          Number(analyticsWindow),
          shellSessionState
        )
        startTransition(() => setAnalyticsBundle(nextAnalytics))
      } else {
        const nextCertifications = await loadFleetCertificationsBundle(shellSessionState)
        startTransition(() => setCertificationsBundle(nextCertifications))
      }
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Fleet data could not be refreshed."
      )
    } finally {
      setIsRefreshing(false)
    }
  }

  async function refreshSelectedIncidentDetail(nextIncidentId: number) {
    const nextDetail = await loadFleetIncidentDetail(nextIncidentId)
    startTransition(() => {
      setDetailBundle(nextDetail)
    })
  }

  async function shareSelectedIncidentEscalationPacket(forceRefresh = false) {
    if (!selectedIncident) {
      return
    }

    const channels = (
      Object.entries(incidentPacketChannels) as Array<
        ["email" | "sms" | "whatsapp", boolean]
      >
    )
      .filter(([, enabled]) => enabled)
      .map(([channel]) => channel)

    if (channels.length === 0) {
      setIncidentPacketActionError("Choose at least one share channel before sending the packet.")
      return
    }

    setIncidentPacketActionError(null)
    setIncidentPacketActionMode(forceRefresh ? "rotate" : "share")

    try {
      const response = await apiClient.shareOperatorIncidentEscalationPacket(selectedIncident.id, {
        channels,
        forceRefresh,
        reason: forceRefresh ? "operator_rotate_and_share" : "operator_share",
      })

      setIncidentPacketStateById((previous) => ({
        ...previous,
        [selectedIncident.id]: {
          attempts: response.data.attempts ?? [],
          expiresAt: response.data.expiresAt ?? null,
          failureCount: response.data.failureCount ?? 0,
          packetId: response.data.packetId ?? null,
          packetStatus: response.data.packetStatus ?? null,
          packetUrl: response.data.packetUrl ?? null,
          packetVersion: response.data.packetVersion ?? null,
          revoked: false,
          revokedAt: null,
          shareStatus: response.data.shareStatus,
          sharedAt: response.data.sharedAt ?? null,
          successCount: response.data.successCount ?? 0,
        },
      }))

      await refreshSelectedIncidentDetail(selectedIncident.id)
    } catch (nextError) {
      setIncidentPacketActionError(
        nextError instanceof Error
          ? nextError.message
          : "Incident escalation packet share failed."
      )
    } finally {
      setIncidentPacketActionMode(null)
    }
  }

  async function revokeSelectedIncidentEscalationPacket() {
    if (!selectedIncident) {
      return
    }

    setIncidentPacketActionError(null)
    setIncidentPacketActionMode("revoke")

    try {
      const response = await apiClient.revokeOperatorIncidentEscalationPacket(
        selectedIncident.id,
        "operator_revoke"
      )

      setIncidentPacketStateById((previous) => ({
        ...previous,
        [selectedIncident.id]: {
          ...previous[selectedIncident.id],
          attempts: previous[selectedIncident.id]?.attempts ?? [],
          expiresAt: null,
          packetId: response.data.packetId ?? null,
          packetStatus: response.data.packetStatus ?? "revoked",
          packetUrl: null,
          revoked: response.data.revoked,
          revokedAt: response.data.revokedAt ?? null,
          shareStatus: "revoked",
          sharedAt: null,
        },
      }))

      await refreshSelectedIncidentDetail(selectedIncident.id)
    } catch (nextError) {
      setIncidentPacketActionError(
        nextError instanceof Error
          ? nextError.message
          : "Incident escalation packet revoke failed."
      )
    } finally {
      setIncidentPacketActionMode(null)
    }
  }

  function updateFleetQrRegistryField<Key extends keyof FleetQrRegistryFormState>(
    key: Key,
    value: FleetQrRegistryFormState[Key]
  ) {
    setFleetQrRegistryForm((previous) => ({
      ...previous,
      [key]: value,
    }))
  }

  async function handleGenerateFleetQrCodes() {
    const quantity = Number.parseInt(fleetQrRegistryForm.quantity, 10)
    const startingNumber = Number.parseInt(fleetQrRegistryForm.startingNumber, 10)
    const fleetSize = Number.parseInt(fleetQrRegistryForm.fleetSize, 10)

    if (!Number.isFinite(quantity) || quantity < 1 || quantity > 250) {
      setError("Truck quantity must be between 1 and 250.")
      return
    }

    if (!Number.isFinite(startingNumber) || startingNumber < 1) {
      setError("Starting number must be 1 or higher.")
      return
    }

    if (!fleetQrRegistryForm.organizationName.trim()) {
      setError("Organization name is required for fleet QR generation.")
      return
    }

    if (
      dashboardBundle?.scope.kind !== "organization"
      && !fleetQrRegistryForm.organizationCity.trim()
    ) {
      setError("Organization city is required when creating a fleet organization.")
      return
    }

    if (!fleetQrRegistryForm.truckPrefix.trim()) {
      setError("Truck number prefix is required.")
      return
    }

    setIsGeneratingTags(true)
    setError(null)

    try {
      const rows = buildFleetQrRows(fleetQrRegistryForm)
      const payload: FleetQrRegisterRequest = {
        generateTags: true,
        rows,
        ...(dashboardBundle?.scope.kind === "organization"
          ? {
              organizationId: dashboardBundle.scope.organizationId,
            }
          : {
              organization: {
                city: fleetQrRegistryForm.organizationCity.trim(),
                country: fleetQrRegistryForm.country.trim() || FLEET_QR_DEFAULT_COUNTRY,
                fleetAdminEmail: fleetQrRegistryForm.notificationEmail.trim() || undefined,
                fleetAdminName: dashboardBundle?.scope.operatorLabel || undefined,
                fleetCategory: "trucking",
                fleetEnabled: true,
                fleetSize: Number.isFinite(fleetSize) ? fleetSize : quantity,
                name: fleetQrRegistryForm.organizationName.trim(),
                notificationEmail: fleetQrRegistryForm.notificationEmail.trim() || undefined,
                region: fleetQrRegistryForm.organizationRegion.trim() || undefined,
                slug:
                  slugifyFleetOrganization(fleetQrRegistryForm.organizationName) || undefined,
                type: "fleet_partner",
              },
            }),
      }

      if (fleetQrRegistryForm.activationMode === "activated") {
        const activatedAt = new Date().toISOString()
        payload.rows = payload.rows.map((row) => ({
          ...row,
          assignmentStatus: "activated",
          status: "active",
          activatedAt,
        }))
      }

      const response = await apiClient.registerFleetQrCodes(payload)
      setLastFleetQrImport(response.data)
      await refresh()
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Fleet QR generation failed."
      )
    } finally {
      setIsGeneratingTags(false)
    }
  }

  const filteredIncidents = useMemo<OperatorIncidentRecord[]>(() => {
    if (!dashboardBundle || mode !== "dashboard") {
      return []
    }

    return [...filterFleetIncidents(dashboardBundle.incidents, {
      ...("incidentStatus" in search ? search : {}),
      incidentQuery: deferredIncidentQuery,
    })].sort((left, right) => {
      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    })
  }, [dashboardBundle, deferredIncidentQuery, mode, search])

  const filteredInventory = useMemo<PublicLookupData[]>(() => {
    if (!dashboardBundle || (mode !== "dashboard" && mode !== "tags")) {
      return []
    }

    return [...filterFleetInventory(dashboardBundle.inventory, {
      ...("tagStatus" in search ? search : {}),
      tagQuery: deferredTagQuery,
    })].sort((left, right) => {
      return left.tag.localeCompare(right.tag)
    })
  }, [dashboardBundle, deferredTagQuery, mode, search])

  const activeIncidentId = incidentId ?? filteredIncidents[0]?.id ?? null

  useEffect(() => {
    if (mode !== "dashboard" || !activeIncidentId) {
      setDetailBundle(null)
      setDetailError(null)
      return
    }

    let cancelled = false
    setDetailError(null)

    loadFleetIncidentDetail(activeIncidentId)
      .then((nextDetail) => {
        if (!cancelled) {
          startTransition(() => {
            setDetailBundle(nextDetail)
          })
        }
      })
      .catch((nextError: unknown) => {
        if (!cancelled) {
          setDetailError(
            nextError instanceof Error
              ? nextError.message
              : "Fleet incident detail could not be loaded."
          )
        }
      })

    return () => {
      cancelled = true
    }
  }, [activeIncidentId, mode])

  useEffect(() => {
    if (mode !== "dashboard") {
      return
    }

    const intervalId = window.setInterval(() => {
      void loadFleetDashboardBundle(shellSessionState)
        .then((nextDashboard) => {
          startTransition(() => {
            setDashboardBundle(nextDashboard)
          })
        })
        .catch(() => {})

      if (!activeIncidentId) {
        return
      }

      void loadFleetIncidentDetail(activeIncidentId)
        .then((nextDetail) => {
          startTransition(() => {
            setDetailBundle(nextDetail)
          })
        })
        .catch(() => {})
    }, FLEET_DASHBOARD_POLL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [activeIncidentId, mode, shellSessionState])

  const filteredCertifications = useMemo<FleetCertificationRecord[]>(() => {
    if (!certificationsBundle || mode !== "certifications") {
      return []
    }

    const query = normalizeToken(certificationQuery)
    const nextRows = certificationsBundle.certifications.filter((record) => {
      if (
        "status" in search
        && search.status
        && search.status !== "all"
        && normalizeToken(record.status) !== normalizeToken(search.status)
      ) {
        return false
      }

      if (
        "program" in search
        && search.program
        && search.program !== "all"
        && normalizeToken(record.programType) !== normalizeToken(search.program)
      ) {
        return false
      }

      if (!query) {
        return true
      }

      const haystack = normalizeToken(
        [
          record.programType,
          record.status,
          record.organization?.name,
          record.organization?.city,
          record.organization?.region,
        ]
          .filter(Boolean)
          .join(" ")
      )

      return haystack.includes(query)
    })

    const sortMode = "sort" in search ? search.sort ?? "artifact" : "artifact"

    return [...nextRows].sort((left, right) => {
      if (sortMode === "score") {
        return Number(right.score || 0) - Number(left.score || 0)
      }

      if (sortMode === "expiring") {
        return new Date(left.expiresAt || 0).getTime() - new Date(right.expiresAt || 0).getTime()
      }

      return new Date(right.awardedAt || right.createdAt || 0).getTime()
        - new Date(left.awardedAt || left.createdAt || 0).getTime()
    })
  }, [certificationQuery, certificationsBundle, mode, search])

  const selectedCertification = filteredCertifications[0] ?? null
  const selectedIncident =
    detailBundle?.detail
    ?? filteredIncidents.find((incident) => incident.id === activeIncidentId)
    ?? null

  useEffect(() => {
    if (!selectedIncident) {
      setIncidentPacketChannels({
        email: true,
        sms: true,
        whatsapp: false,
      })
      setIncidentPacketActionError(null)
      setIncidentPacketActionMode(null)
      return
    }

    setIncidentPacketChannels({
      email: true,
      sms: true,
      whatsapp: isCriticalOrSevereIncident(selectedIncident),
    })
    setIncidentPacketActionError(null)
    setIncidentPacketActionMode(null)
  }, [selectedIncident])

  const canManageIncidentEscalationPacket =
    shellSessionState?.status === "ready"
    && (
      shellSessionState.session.permissions.manageExports
      || shellSessionState.session.permissions.exportEvidence
      || shellSessionState.session.permissions.updateAssignments
    )
  const selectedIncidentPacketState = mergeIncidentEscalationPacketState(
    buildIncidentEscalationPacketStateFromRecord(selectedIncident),
    selectedIncident ? incidentPacketStateById[selectedIncident.id] ?? null : null
  )
  const selectedIncidentPacketToken = parseIncidentEscalationPacketToken(
    selectedIncidentPacketState?.packetUrl
  )
  const selectedIncidentPacketHasDeliverySummary = Boolean(
    selectedIncidentPacketState
    && (
      selectedIncidentPacketState.attempts?.length
      || selectedIncidentPacketState.failureCount !== undefined
      || selectedIncidentPacketState.shareStatus
      || selectedIncidentPacketState.successCount !== undefined
    )
  )
  const selectedIncidentHasPacketHistory = selectedIncident
    ? hasIncidentEscalationPacketHistory(selectedIncident)
    : false
  const selectedIncidentCoordinates = selectedIncident
    ? formatCoordinates(selectedIncident.gpsLatitude, selectedIncident.gpsLongitude)
    : "Not recorded"
  const selectedIncidentCoordinatesHref = selectedIncident
    ? buildCoordinatesMapHref(selectedIncident.gpsLatitude, selectedIncident.gpsLongitude)
    : null
  const selectedIncidentPhotoEvidence = selectedIncident
    ? (selectedIncident.incidentMedia ?? []).filter((media) => {
        return Boolean(media.url) && isLikelyPhotoEvidenceMedia(media.mimeType)
      })
    : []
  const selectedIncidentVoiceEvidence = selectedIncident
    ? (selectedIncident.voiceNotes ?? []).filter((voiceNote) => Boolean(voiceNote.url))
    : []
  const latestSelectedIncidentUpdates = useMemo(() => {
    if (!selectedIncident?.updates?.length) {
      return []
    }

    return [...selectedIncident.updates]
      .sort((left, right) => {
        return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime()
      })
      .slice(0, 3)
  }, [selectedIncident])
  const latestSelectedIncidentTimeline = useMemo(() => {
    if (!selectedIncident?.timeline?.length) {
      return []
    }

    return [...selectedIncident.timeline]
      .sort((left, right) => {
        return new Date(right.eventAt || 0).getTime() - new Date(left.eventAt || 0).getTime()
      })
      .slice(0, 3)
  }, [selectedIncident])
  const dashboardIncidentMetrics = useMemo(() => {
    const incidents = dashboardBundle?.incidents ?? []
    const recent24h = dashboardBundle?.stats?.recent_24h
    const open = dashboardBundle?.stats?.open
    const criticalOrSevereOpen = dashboardBundle?.stats?.critical_or_severe_open
    const needsAttentionNow = dashboardBundle?.stats?.needs_attention_now

    return {
      criticalOrSevereOpen: criticalOrSevereOpen
        ?? incidents.filter((incident) => {
          return isOpenIncident(incident) && isCriticalOrSevereIncident(incident)
        }).length,
      needsAttentionNow: needsAttentionNow
        ?? incidents.filter((incident) => requiresAttentionNow(incident)).length,
      open: open
        ?? incidents.filter((incident) => isOpenIncident(incident)).length,
      recent24h: recent24h ?? incidents.filter((incident) => {
        const reportedAt = getIncidentReportedTime(incident)
        if (!reportedAt) {
          return false
        }
        return Date.now() - new Date(reportedAt).getTime() <= 24 * 60 * 60 * 1000
      }).length,
    }
  }, [dashboardBundle])
  const analyticsScopeSummary =
    analyticsBundle && mode === "analytics"
      ? getFleetAnalyticsScopeSummary(analyticsBundle.scope)
      : null
  const dashboardScopeSummary =
    dashboardBundle && (mode === "dashboard" || mode === "tags")
      ? getFleetWorkspaceScopeSummary(dashboardBundle.scope, mode)
      : null
  const certificationScopeSummary =
    certificationsBundle && mode === "certifications"
      ? getFleetWorkspaceScopeSummary(certificationsBundle.scope, "certifications")
      : null

  return (
    <>
      {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
      {isLoading ? (
        <FleetWorkspaceLoadingSkeleton mode={mode} />
      ) : null}

      {dashboardBundle && mode === "dashboard" ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Incidents in last 24h"
              value={dashboardIncidentMetrics.recent24h}
              detail={
                dashboardBundle.scope.kind === "organization"
                  ? "Recent incident activity for the active fleet organization."
                  : "Recent incident activity across visible fleet organizations."
              }
            />
            <OperatorMetricCard
              label="Open incidents"
              value={dashboardIncidentMetrics.open}
              detail={
                dashboardBundle.scope.kind === "organization"
                  ? "Open incidents currently assigned to the active organization scope."
                  : "Open incidents currently visible across global fleet scope."
              }
            />
            <OperatorMetricCard
              label="Critical/Severe open"
              value={dashboardIncidentMetrics.criticalOrSevereOpen}
              detail="Open incidents currently marked critical or severe."
            />
            <OperatorMetricCard
              label="Needs attention now"
              value={dashboardIncidentMetrics.needsAttentionNow}
              detail="Open incidents requiring immediate operator review."
            />
          </section>

          <OperatorStatusMessage>
            <strong>{dashboardScopeSummary?.title}.</strong>{" "}
            {dashboardScopeSummary?.headline} {dashboardScopeSummary?.note}
          </OperatorStatusMessage>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
            <OperatorPanel
              title="Incident list"
              description={
                dashboardBundle.scope.kind === "organization"
                  ? "Operational incident feed for the active fleet organization."
                  : "Operational incident feed across all visible fleet organizations."
              }
            >
              <div className="flex flex-col gap-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Incident search</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={"incidentQuery" in search ? search.incidentQuery ?? "" : ""}
                      onChange={(event) =>
                        onSearchChange({ incidentQuery: event.target.value || undefined })
                      }
                      placeholder="Incident, tag, plate, or contact"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={"incidentStatus" in search ? search.incidentStatus ?? "all" : "all"}
                      onChange={(event) =>
                        onSearchChange({
                          incidentStatus:
                            event.target.value === "all"
                              ? undefined
                              : (event.target.value as FleetDashboardSearch["incidentStatus"]),
                        })
                      }
                    >
                      {FLEET_INCIDENT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status === "all" ? "All statuses" : titleizeToken(status)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {filteredIncidents.length === 0 ? (
                  <OperatorEmptyState
                    title="No fleet incidents match the current filters."
                    message="Try adjusting the current filters to review another incident."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredIncidents.map((incident) => (
                      <button
                        key={incident.id}
                        type="button"
                        onClick={() => onSelectIncident(incident.id)}
                        className={cn(
                          "rounded-3xl border px-4 py-4 text-left transition-colors",
                          selectedIncident?.id === incident.id
                            ? "border-primary/45 bg-primary/8"
                            : "border-border/70 bg-background/70 hover:border-primary/25"
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm text-muted-foreground">
                              Incident #{incident.id} · Reported {formatDateTime(getIncidentReportedTime(incident))}
                            </p>
                            <h3 className="text-lg font-semibold">
                              {titleizeToken(incident.type || incident.reportKind || "incident")}
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Source/location: {getIncidentSourceLabel(incident)} · {getIncidentLocationLabel(incident)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                              {titleizeToken(resolveIncidentStatus(incident))}
                            </span>
                            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                              {titleizeToken(incident.severity)}
                            </span>
                            {requiresAttentionNow(incident) ? (
                              <span className="rounded-full bg-destructive/15 px-3 py-1 text-xs font-semibold text-destructive">
                                Attention required
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 text-sm text-muted-foreground md:grid-cols-2 xl:grid-cols-4">
                          <span>Lifecycle status: {titleizeToken(resolveIncidentStatus(incident))}</span>
                          <span>Coordinates: {formatCoordinates(incident.gpsLatitude, incident.gpsLongitude)}</span>
                          <span>Photos: {incident.incidentMedia?.length ?? 0}</span>
                          <span>Voice notes: {incident.voiceNotes?.length ?? 0}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </OperatorPanel>

            <div className="grid gap-5">
              <OperatorPanel
                title="Selected incident detail"
                description="Read-only operational detail for the currently selected incident."
              >
                {!selectedIncident ? (
                  <OperatorEmptyState
                    title="Select an incident from the incident list."
                    message="Choose any incident row to review source details, latest updates, and timeline activity."
                  />
                ) : (
                  <div className="grid gap-3">
                    {detailError ? (
                      <OperatorStatusMessage tone="warning">
                        {detailError}
                      </OperatorStatusMessage>
                    ) : null}
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4 text-sm">
                      <div className="font-semibold">
                        Incident #{selectedIncident.id} · {titleizeToken(selectedIncident.type || selectedIncident.reportKind || "incident")}
                      </div>
                      <div className="mt-2 text-muted-foreground">
                        Full status: {titleizeToken(resolveIncidentStatus(selectedIncident))}
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        Location: {getIncidentLocationLabel(selectedIncident)}
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        Coordinates:{" "}
                        {selectedIncidentCoordinatesHref ? (
                          <a
                            className="font-medium text-primary underline"
                            href={selectedIncidentCoordinatesHref}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {selectedIncidentCoordinates}
                          </a>
                        ) : (
                          selectedIncidentCoordinates
                        )}
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Reporter/source</div>
                        <div className="mt-1 font-medium">{getIncidentSourceLabel(selectedIncident)}</div>
                        <div className="mt-1 text-muted-foreground">
                          Contact: {selectedIncident.contact?.name || "Not recorded"}
                          {selectedIncident.contact?.phone ? ` · ${selectedIncident.contact.phone}` : ""}
                          {selectedIncident.contact?.email ? ` · ${selectedIncident.contact.email}` : ""}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Short description</div>
                        <div className="mt-1">
                          {selectedIncident.description
                            || selectedIncident.locationDescription
                            || "No narrative provided"}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Evidence summary</div>
                        <div className="mt-1">Photos: {selectedIncidentPhotoEvidence.length}</div>
                        <div className="mt-1">Voice notes: {selectedIncidentVoiceEvidence.length}</div>
                        <div className="mt-1">Assignments: {selectedIncident.assignments?.length ?? 0}</div>
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Dispatch posture</div>
                        <div className="mt-1">Active dispatches: {selectedIncident.dispatchSummary?.active ?? 0}</div>
                        <div className="mt-1">Total dispatches: {selectedIncident.dispatchSummary?.total ?? 0}</div>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        Incident escalation packet
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        {selectedIncidentPacketState?.packetUrl
                          ? "The current public packet link is ready to open or send."
                          : selectedIncidentHasPacketHistory
                            ? "Escalation packet history exists for this incident. Share again to surface the current public link in this operator session."
                            : "No escalation packet link has been generated in this operator session yet."}
                      </div>
                      {incidentPacketActionError ? (
                        <div className="mt-3">
                          <OperatorStatusMessage tone="warning">
                            {incidentPacketActionError}
                          </OperatorStatusMessage>
                        </div>
                      ) : null}
                      {canManageIncidentEscalationPacket ? (
                        <>
                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            {(
                              [
                                ["email", "Email share"],
                                ["sms", "SMS share"],
                                ["whatsapp", "WhatsApp share"],
                              ] as const
                            ).map(([channel, label]) => {
                              const disabled =
                                channel === "whatsapp"
                                && !isCriticalOrSevereIncident(selectedIncident)

                              return (
                                <label
                                  key={channel}
                                  className={cn(
                                    "flex items-center gap-3 rounded-2xl border border-border/70 px-3 py-3 text-sm",
                                    disabled ? "opacity-60" : "bg-background"
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={incidentPacketChannels[channel]}
                                    disabled={disabled || incidentPacketActionMode !== null}
                                    onChange={(event) => {
                                      setIncidentPacketChannels((previous) => ({
                                        ...previous,
                                        [channel]: event.target.checked,
                                      }))
                                    }}
                                  />
                                  <span>{label}</span>
                                </label>
                              )
                            })}
                          </div>
                          {!isCriticalOrSevereIncident(selectedIncident) ? (
                            <div className="mt-3 text-sm text-muted-foreground">
                              WhatsApp packet delivery is only enabled by the backend for critical or severe incidents.
                            </div>
                          ) : null}
                          <div className="mt-4 flex flex-wrap gap-3">
                            <button
                              type="button"
                              disabled={incidentPacketActionMode !== null}
                              onClick={() => void shareSelectedIncidentEscalationPacket(false)}
                              className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                            >
                              {incidentPacketActionMode === "share" ? "Sharing…" : "Share packet"}
                            </button>
                            <button
                              type="button"
                              disabled={incidentPacketActionMode !== null}
                              onClick={() => void shareSelectedIncidentEscalationPacket(true)}
                              className="rounded-2xl border border-border px-4 py-2 text-sm font-medium hover:bg-background disabled:opacity-60"
                            >
                              {incidentPacketActionMode === "rotate" ? "Rotating…" : "Rotate token + reshare"}
                            </button>
                            <button
                              type="button"
                              disabled={incidentPacketActionMode !== null}
                              onClick={() => void revokeSelectedIncidentEscalationPacket()}
                              className="rounded-2xl border border-destructive/45 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/8 disabled:opacity-60"
                            >
                              {incidentPacketActionMode === "revoke" ? "Revoking…" : "Revoke packet"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="mt-4 text-sm text-muted-foreground">
                          This operator role cannot share or revoke incident escalation packets.
                        </div>
                      )}
                      {selectedIncidentPacketState ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                            <div className="text-muted-foreground">Packet access</div>
                            <div className="mt-2 font-medium">
                              {selectedIncidentPacketState.packetUrl ? (
                                <a
                                  className="break-all text-primary underline"
                                  href={selectedIncidentPacketState.packetUrl}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  {selectedIncidentPacketState.packetUrl}
                                </a>
                              ) : (
                                "No active link returned"
                              )}
                            </div>
                            <div className="mt-2 text-muted-foreground">
                              Token:{" "}
                              <span className="break-all font-mono text-xs text-foreground">
                                {selectedIncidentPacketToken || "Not available"}
                              </span>
                            </div>
                            <div className="mt-1 text-muted-foreground">
                              Expires: {formatDateTime(selectedIncidentPacketState.expiresAt)}
                            </div>
                          </div>
                          <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                            <div className="text-muted-foreground">Packet status</div>
                            <div className="mt-2 font-medium">
                              {titleizeToken(selectedIncidentPacketState.packetStatus || "unknown")}
                            </div>
                            {selectedIncidentPacketHasDeliverySummary ? (
                              <>
                                {selectedIncidentPacketState.shareStatus ? (
                                  <div className="mt-1 text-muted-foreground">
                                    Share result: {titleizeToken(selectedIncidentPacketState.shareStatus)}
                                  </div>
                                ) : null}
                                {selectedIncidentPacketState.successCount !== undefined
                                  || selectedIncidentPacketState.failureCount !== undefined ? (
                                    <div className="mt-1 text-muted-foreground">
                                      Success / failure: {selectedIncidentPacketState.successCount ?? 0} / {selectedIncidentPacketState.failureCount ?? 0}
                                    </div>
                                  ) : null}
                              </>
                            ) : null}
                            <div className="mt-1 text-muted-foreground">
                              Shared at: {formatDateTime(selectedIncidentPacketState.sharedAt)}
                            </div>
                            {selectedIncidentPacketState.revokedAt ? (
                              <div className="mt-1 text-muted-foreground">
                                Revoked at: {formatDateTime(selectedIncidentPacketState.revokedAt)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : selectedIncidentHasPacketHistory ? (
                        <div className="mt-4 rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                          Existing packet history was detected from export records, but the current public token has not been loaded into this session yet.
                        </div>
                      ) : null}
                      {selectedIncidentPacketState?.attempts?.length ? (
                        <div className="mt-4 grid gap-2">
                          {selectedIncidentPacketState.attempts.map((attempt, index) => (
                            <div
                              key={`${attempt.deliveryId || attempt.recipient || attempt.channel || "attempt"}-${index}`}
                              className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                            >
                              <div className="font-medium">
                                {titleizeToken(attempt.channel || "channel")} · {titleizeToken(attempt.status || "pending")}
                              </div>
                              <div className="mt-1 text-muted-foreground">
                                {attempt.recipient || "No recipient"} · {attempt.recipientSource || "No source"}
                              </div>
                              {attempt.reason ? (
                                <div className="mt-1 text-muted-foreground">
                                  Reason: {attempt.reason}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Photo evidence</div>
                        {selectedIncidentPhotoEvidence.length === 0 ? (
                          <div className="mt-2 text-muted-foreground">No photos attached.</div>
                        ) : (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {selectedIncidentPhotoEvidence.slice(0, 4).map((media, index) => (
                              <a
                                key={media.id}
                                className="block overflow-hidden rounded-xl border border-border/70"
                                href={media.url}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <img
                                  alt={`Incident photo ${index + 1}`}
                                  className="h-24 w-full object-cover"
                                  loading="lazy"
                                  src={media.url}
                                />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="text-muted-foreground">Voice notes</div>
                        {selectedIncidentVoiceEvidence.length === 0 ? (
                          <div className="mt-2 text-muted-foreground">No voice notes attached.</div>
                        ) : (
                          <div className="mt-2 grid gap-2">
                            {selectedIncidentVoiceEvidence.slice(0, 3).map((voiceNote, index) => (
                              <div
                                key={voiceNote.id}
                                className="rounded-xl border border-border/70 bg-background/70 px-2 py-2"
                              >
                                <div className="mb-2 text-xs text-muted-foreground">
                                  Voice note {index + 1}
                                </div>
                                <audio
                                  aria-label={`Incident voice note ${index + 1}`}
                                  className="w-full"
                                  controls
                                  preload="none"
                                  src={voiceNote.url}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        Latest updates
                      </div>
                      <div className="mt-3 grid gap-2">
                        {latestSelectedIncidentUpdates.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No update notes recorded.</p>
                        ) : (
                          latestSelectedIncidentUpdates.map((update) => (
                            <div
                              key={update.id}
                              className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                            >
                              <div className="text-muted-foreground">
                                {formatDateTime(update.createdAt)} · {update.updatedBy || "System"}
                              </div>
                              <div className="mt-1">{update.updateText || "No note text."}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        Latest timeline
                      </div>
                      <div className="mt-3 grid gap-2">
                        {latestSelectedIncidentTimeline.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No timeline events recorded.</p>
                        ) : (
                          latestSelectedIncidentTimeline.map((event) => (
                            <div
                              key={event.id}
                              className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                            >
                              <div className="text-muted-foreground">
                                {formatDateTime(event.eventAt)} · {titleizeToken(event.eventType)}
                              </div>
                              <div className="mt-1">{event.action || "Lifecycle event recorded."}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => void refresh()}
                        disabled={isRefreshing}
                        className="rounded-2xl border border-border px-3 py-2 text-sm font-medium hover:bg-background"
                      >
                        {isRefreshing ? "Refreshing…" : "Refresh incidents"}
                      </button>
                      {requiresAttentionNow(selectedIncident) ? (
                        <div className="rounded-2xl bg-destructive/12 px-3 py-2 text-sm font-medium text-destructive">
                          Attention required
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </OperatorPanel>
            </div>
          </section>
        </>
      ) : null}

      {dashboardBundle && mode === "tags" ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Fleet tags"
              value={dashboardBundle.inventory.length}
              detail="Fleet-owned QR tags currently visible in this scope."
            />
            <OperatorMetricCard
              label="Ready to report"
              value={
                dashboardBundle.inventory.filter((item) => item.publicReportingReady).length
              }
              detail="Tags that scan directly into the report flow."
            />
            <OperatorMetricCard
              label="Registration required"
              value={
                dashboardBundle.inventory.filter((item) => !item.publicReportingReady).length
              }
              detail="Tags that still route into registration first."
            />
            <OperatorMetricCard
              label="Last batch"
              value={lastFleetQrImport?.import.created ?? 0}
              detail="Newly created tags from the most recent fleet QR generation action."
            />
          </section>

          <OperatorStatusMessage>
            <strong>{dashboardScopeSummary?.title}.</strong>{" "}
            {dashboardScopeSummary?.headline} {dashboardScopeSummary?.note}
          </OperatorStatusMessage>

          <OperatorStatusMessage>
            Structured enterprise onboarding now starts under <strong>Fleet Onboarding</strong> and
            <strong> Fleet Kits</strong>. This registry remains available in Phase 1 for tag audit,
            scan lookup, and bridge QR generation while the dedicated B2B asset workflow is rolled
            out.
          </OperatorStatusMessage>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
            <OperatorPanel
              title="Bridge truck QR generation"
              description="Create truck-linked fleet QR tags inside the Fleet workspace while the dedicated onboarding and kit issuance flow replaces the old flat generator."
            >
              <div className="grid gap-4">
                {dashboardBundle.scope.kind !== "organization" ? (
                  <div className="rounded-3xl border border-primary/20 bg-primary/5 px-4 py-4 text-sm text-muted-foreground">
                    This session is global. Filling the organization fields below will create or
                    update a dedicated fleet organization before generating truck tags.
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Organization name</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.organizationName}
                      onChange={(event) =>
                        updateFleetQrRegistryField("organizationName", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Fleet label</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.fleetName}
                      onChange={(event) =>
                        updateFleetQrRegistryField("fleetName", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Truck prefix</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.truckPrefix}
                      onChange={(event) =>
                        updateFleetQrRegistryField("truckPrefix", event.target.value.toUpperCase())
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Truck quantity</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      inputMode="numeric"
                      value={fleetQrRegistryForm.quantity}
                      onChange={(event) =>
                        updateFleetQrRegistryField("quantity", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Starting number</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      inputMode="numeric"
                      value={fleetQrRegistryForm.startingNumber}
                      onChange={(event) =>
                        updateFleetQrRegistryField("startingNumber", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Activation mode</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.activationMode}
                      onChange={(event) =>
                        updateFleetQrRegistryField(
                          "activationMode",
                          event.target.value as FleetQrActivationMode
                        )
                      }
                    >
                      <option value="activated">Report-ready immediately</option>
                      <option value="registration_required">Require registration first</option>
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Fleet size</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      inputMode="numeric"
                      value={fleetQrRegistryForm.fleetSize}
                      onChange={(event) =>
                        updateFleetQrRegistryField("fleetSize", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Notification email</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      type="email"
                      value={fleetQrRegistryForm.notificationEmail}
                      onChange={(event) =>
                        updateFleetQrRegistryField("notificationEmail", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Vehicle make</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.vehicleMake}
                      onChange={(event) =>
                        updateFleetQrRegistryField("vehicleMake", event.target.value)
                      }
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Vehicle model</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={fleetQrRegistryForm.vehicleModel}
                      onChange={(event) =>
                        updateFleetQrRegistryField("vehicleModel", event.target.value)
                      }
                    />
                  </label>
                </div>

                {dashboardBundle.scope.kind !== "organization" ? (
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">City</span>
                      <input
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={fleetQrRegistryForm.organizationCity}
                        onChange={(event) =>
                          updateFleetQrRegistryField("organizationCity", event.target.value)
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Region</span>
                      <input
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={fleetQrRegistryForm.organizationRegion}
                        onChange={(event) =>
                          updateFleetQrRegistryField("organizationRegion", event.target.value)
                        }
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Country</span>
                      <input
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={fleetQrRegistryForm.country}
                        onChange={(event) =>
                          updateFleetQrRegistryField("country", event.target.value)
                        }
                      />
                    </label>
                  </div>
                ) : null}

                <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4 text-sm text-muted-foreground">
                  This bridge flow still creates one fleet-tag row per truck number. The long-term
                  B2B path is organization-first onboarding, asset roster creation, and
                  system-activated kits. When set to <strong>report-ready immediately</strong>,
                  scans go straight to the incident report surface instead of the public
                  registration form.
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void handleGenerateFleetQrCodes()}
                    disabled={isGeneratingTags}
                    className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {isGeneratingTags ? "Generating QR codes…" : "Generate fleet QR codes"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFleetQrRegistryForm(createInitialFleetQrRegistryForm(dashboardBundle.scope))
                    }
                    disabled={isGeneratingTags}
                    className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </OperatorPanel>

            <div className="grid gap-5">
              <OperatorPanel
                title="Latest generated tags"
                description="Review the truck-to-tag mapping from the most recent generation action."
              >
                {!lastFleetQrImport?.import.rows.length ? (
                  <OperatorEmptyState
                    title="No fleet QR batch generated in this session yet."
                    message="Generate a batch to review the QR URLs and truck number mapping here."
                  />
                ) : (
                  <div className="grid gap-3">
                    {lastFleetQrImport.import.rows
                      .filter((row) => row.tag)
                      .map((row) => (
                        <div
                          key={`${row.rowIndex}-${row.tag}`}
                          className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-medium">{row.data?.plateNumber || row.tag}</div>
                              <div className="text-sm text-muted-foreground">{row.tag}</div>
                              <a
                                className="mt-2 block text-sm text-primary underline"
                                href={`https://traffic.feedbacknfc.com/t/${row.tag}`}
                                rel="noreferrer"
                                target="_blank"
                              >
                                {`https://traffic.feedbacknfc.com/t/${row.tag}`}
                              </a>
                            </div>
                            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                              {row.status}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </OperatorPanel>

              <OperatorPanel
                title="Fleet QR registry"
                description="Review current truck assignments, scan destinations, and sticker audit state inside the Fleet product."
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Tag search</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={"tagQuery" in search ? search.tagQuery ?? "" : ""}
                      onChange={(event) =>
                        onSearchChange({ tagQuery: event.target.value || undefined })
                      }
                      placeholder="Tag, truck number, or assignee"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={"tagStatus" in search ? search.tagStatus ?? "" : ""}
                      onChange={(event) =>
                        onSearchChange({ tagStatus: event.target.value || undefined })
                      }
                      placeholder="active, assigned, inactive"
                    />
                  </label>
                </div>

                <div className="mt-4 grid gap-3">
                  {filteredInventory.length === 0 ? (
                    <OperatorEmptyState
                      title="No truck tags match the current fleet filters."
                      message="Generate a new fleet QR batch or adjust the current filters."
                    />
                  ) : (
                    filteredInventory.slice(0, 12).map((item) => (
                      <div
                        key={item.tag}
                        className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-medium">
                              {item.vehicle?.plateNumber || item.assignment?.externalRef || item.tag}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {item.tag} · {item.assignment?.assigneeName || "Unassigned"}
                            </div>
                            <a
                              className="mt-2 block text-sm text-primary underline"
                              href={`https://traffic.feedbacknfc.com/t/${item.tag}`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {`https://traffic.feedbacknfc.com/t/${item.tag}`}
                            </a>
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <div>{item.publicReportingReady ? "Report-ready" : "Registration first"}</div>
                            <div>{titleizeToken(item.device?.status)}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </OperatorPanel>
            </div>
          </section>
        </>
      ) : null}

      {analyticsBundle && mode === "analytics" ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Timeline days"
              value={("days" in search ? search.days : "30") ?? "30"}
              detail={
                analyticsBundle.scope.kind === "organization"
                  ? `${analyticsBundle.scope.organizationName || "The assigned organization"} is the active scope for this analytics window.`
                  : "This analytics view is showing the global session scope."
              }
            />
            <OperatorMetricCard
              label="Resolution rate"
              value={`${analyticsBundle.performance?.resolutionRate ?? 0}%`}
              detail={`${analyticsBundle.performance?.resolvedIncidents ?? 0} resolved of ${analyticsBundle.performance?.totalIncidents ?? 0} total incidents.`}
            />
            <OperatorMetricCard
              label="Top hotspot"
              value={analyticsBundle.hotspots[0]?.locationName || "No hotspot"}
              detail={`Weighted score ${analyticsBundle.hotspots[0]?.weightedHotspotScore ?? 0}.`}
            />
            <OperatorMetricCard
              label="Safety pressure"
              value={analyticsBundle.safetyScores.reduce((sum, item) => sum + item.totalImpact, 0)}
              detail="Combined impact across the safety-score lanes in this window."
            />
          </section>

          <OperatorStatusMessage>
            <strong>{analyticsScopeSummary?.title}.</strong> {analyticsScopeSummary?.headline}{" "}
            {analyticsScopeSummary?.note}
          </OperatorStatusMessage>

          <OperatorPanel
            title="Analytics read views"
            description="Review timeline, hotspots, safety scores, and breakdowns for the selected analytics window."
          >
            <div className="grid gap-3 md:grid-cols-4">
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-muted-foreground">Window</span>
                <select
                  className="rounded-2xl border border-border bg-background px-3 py-2"
                  value={"days" in search ? search.days ?? "30" : "30"}
                  onChange={(event) =>
                    onSearchChange({
                      days: event.target.value as FleetAnalyticsSearch["days"],
                    })
                  }
                >
                  {FLEET_ANALYTICS_WINDOWS.map((windowSize) => (
                    <option key={windowSize} value={windowSize}>
                      Last {windowSize} days
                    </option>
                  ))}
                </select>
              </label>
              <div className="md:col-span-3 flex items-end justify-end">
                <button
                  type="button"
                  onClick={() => void refresh()}
                  disabled={isRefreshing}
                  className="rounded-2xl border border-border px-3 py-2 text-sm font-medium hover:bg-background"
                >
                  {isRefreshing ? "Refreshing…" : "Refresh analytics"}
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Daily timeline
                </div>
                <div className="mt-3 grid gap-2">
                  {analyticsBundle.timeline.slice(-6).map((point) => (
                    <div
                      key={point.date}
                      className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                    >
                      {point.date} • total {point.total} • severe {point.severe} • resolved{" "}
                      {point.resolved}
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Hotspots
                </div>
                <div className="mt-3 grid gap-2">
                  {analyticsBundle.hotspots.slice(0, 4).map((hotspot) => (
                    <div
                      key={`${hotspot.locationId ?? hotspot.locationName}`}
                      className="rounded-2xl border border-border/70 bg-background px-3 py-3"
                    >
                      <div className="font-medium">
                        {hotspot.locationName || "Unnamed location"}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {hotspot.totalIncidents ?? 0} incidents • hotspot weight{" "}
                        {hotspot.weightedHotspotScore ?? 0}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Safety-score lanes
                </div>
                <div className="mt-3 grid gap-2">
                  {analyticsBundle.safetyScores.slice(0, 5).map((item) => (
                    <div
                      key={item.tag}
                      className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                    >
                      {item.tag} • {item.incidentCount} incidents • impact {item.totalImpact}
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Hourly distribution
                </div>
                <div className="mt-3 grid gap-2">
                  {analyticsBundle.hourlyDistribution
                    .filter((row) => row.count > 0)
                    .slice(0, 6)
                    .map((row) => (
                      <div
                        key={row.hour}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                      >
                        {String(row.hour).padStart(2, "0")}:00 • {row.count} reports
                      </div>
                    ))}
                </div>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  Breakdown snapshot
                </div>
                <div className="mt-3 grid gap-2">
                  {(analyticsBundle.breakdown?.byType ?? []).slice(0, 5).map((row) => (
                    <div
                      key={row.type}
                      className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground"
                    >
                      {titleizeToken(row.type)} • {row.count}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </OperatorPanel>
        </>
      ) : null}

      {certificationsBundle && mode === "certifications" ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              label="Current artifacts"
              value={filteredCertifications.filter((record) =>
                ["awarded", "active", "current"].includes(normalizeToken(record.status))
              ).length}
              detail="Certification records currently marked active or current."
            />
            <OperatorMetricCard
              label="Average score"
              value={
                filteredCertifications.length > 0
                  ? (
                      filteredCertifications.reduce(
                        (sum, record) => sum + Number(record.score || 0),
                        0
                      ) / filteredCertifications.length
                    ).toFixed(1)
                  : "0.0"
              }
              detail="Score cards are read straight from the backend certification payload."
            />
            <OperatorMetricCard
              label="Positive actions"
              value={filteredCertifications.reduce(
                (sum, record) => sum + Number(record.positiveActionCount || 0),
                0
              )}
              detail="Positive-action counts are preserved exactly from the certification records."
            />
            <OperatorMetricCard
              label="Evidence summaries"
              value={filteredCertifications.filter((record) => record.evidenceSnapshot !== undefined).length}
              detail="Evidence summaries are available for the records that include them."
            />
          </section>

          <OperatorStatusMessage>
            <strong>{certificationScopeSummary?.title}.</strong>{" "}
            {certificationScopeSummary?.headline} {certificationScopeSummary?.note}
          </OperatorStatusMessage>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
            <OperatorPanel
              title="Certification roster"
              description={
                certificationsBundle.scope.kind === "organization"
                  ? "Filter and review certification records for the active organization."
                  : "Filter and review certification records across all visible organizations."
              }
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-muted-foreground">Search</span>
                  <input
                    className="rounded-2xl border border-border bg-background px-3 py-2"
                    value={"q" in search ? search.q ?? "" : ""}
                    onChange={(event) => onSearchChange({ q: event.target.value || undefined })}
                    placeholder="Program, status, city"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <input
                    className="rounded-2xl border border-border bg-background px-3 py-2"
                    value={"status" in search ? search.status ?? "" : ""}
                    onChange={(event) =>
                      onSearchChange({ status: event.target.value || undefined })
                    }
                    placeholder="current, active, expired"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-muted-foreground">Program</span>
                  <input
                    className="rounded-2xl border border-border bg-background px-3 py-2"
                    value={"program" in search ? search.program ?? "" : ""}
                    onChange={(event) =>
                      onSearchChange({ program: event.target.value || undefined })
                    }
                    placeholder="gold, silver, trusted"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-muted-foreground">Sort</span>
                  <select
                    className="rounded-2xl border border-border bg-background px-3 py-2"
                    value={"sort" in search ? search.sort ?? "artifact" : "artifact"}
                    onChange={(event) =>
                      onSearchChange({
                        sort: event.target.value as FleetCertificationsSearch["sort"],
                      })
                    }
                  >
                    {FLEET_CERTIFICATION_SORTS.map((sortMode) => (
                      <option key={sortMode} value={sortMode}>
                        {titleizeToken(sortMode)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 grid gap-3">
                {filteredCertifications.length === 0 ? (
                  <OperatorEmptyState
                    title="No certification records match the current filters."
                    message="Try adjusting the current filters to review another certification record."
                  />
                ) : (
                  filteredCertifications.map((record) => (
                    <div
                      key={record.id}
                      className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium">
                            {titleizeToken(record.programType)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {record.organization?.name
                              || certificationsBundle.scope.organizationName
                              || "Visible organization"}
                          </div>
                        </div>
                        <span
                          className={cn(
                            "rounded-full px-3 py-1 text-xs font-medium",
                            certificationTone(record)
                          )}
                        >
                          {titleizeToken(record.status)}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
                        <span>Score: {record.score ?? "Unscored"}</span>
                        <span>Positive actions: {record.positiveActionCount ?? 0}</span>
                        <span>Incident-free days: {record.incidentFreeDays ?? 0}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </OperatorPanel>

            <OperatorPanel
              title="Certification detail"
              description={
                certificationsBundle.scope.kind === "organization"
                  ? "Review the details of the selected certification record."
                  : "Review the details of the selected certification record across visible organizations."
              }
            >
              {!selectedCertification ? (
                <OperatorEmptyState
                  title="No certification selected."
                  message="The detail card uses the first filtered certification record when one is available."
                />
              ) : (
                <div className="grid gap-3">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="font-medium">
                      {titleizeToken(selectedCertification.programType)}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {selectedCertification.organization?.name
                        || certificationsBundle.scope.organizationName
                        || "Visible organization"}
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                      <div className="text-sm text-muted-foreground">Awarded</div>
                      <div className="mt-2 font-medium">
                        {formatDateTime(selectedCertification.awardedAt)}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                      <div className="text-sm text-muted-foreground">Expires</div>
                      <div className="mt-2 font-medium">
                        {formatDateTime(selectedCertification.expiresAt)}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background px-4 py-4 text-sm leading-6 text-muted-foreground">
                    Qualifying window: {selectedCertification.qualifyingWindow?.start || "—"} to{" "}
                    {selectedCertification.qualifyingWindow?.end || "—"}
                    <br />
                    Evidence snapshot available:{" "}
                    {selectedCertification.evidenceSnapshot !== undefined ? "Yes" : "No"}
                    <br />
                    Metadata available: {selectedCertification.metadata !== undefined ? "Yes" : "No"}
                  </div>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    disabled={isRefreshing}
                    className="rounded-2xl border border-border px-3 py-2 text-sm font-medium hover:bg-background"
                  >
                    {isRefreshing ? "Refreshing…" : "Refresh certifications"}
                  </button>
                </div>
              )}
            </OperatorPanel>
          </section>
        </>
      ) : null}
    </>
  )
}
